/**
 * List Detail Screen
 *
 * Shows a single list with its items and metadata.
 * Supports infinite scroll, sort options, and permission-based actions.
 */

import type { ListItemsParams } from '@/api/endpoints/lists';
import {
  listDetailQueryOptions,
  listItemsInfiniteQueryOptions,
} from '@/api/queries/lists';
import { QueryState } from '@/components/layout/query-state';
import { ScreenHeader } from '@/components/layout/screen-header';
import { PageTitle } from '@/components/seo/page-title';
import { EditListModal } from '@/features/lists/components/edit-list-modal';
import { EditNoteModal } from '@/features/lists/components/edit-note-modal';
import { ListDetailHeader } from '@/features/lists/components/list-detail-header';
import { ListInvitationBanner } from '@/features/lists/components/list-invitation-banner';
import { ListItemRow } from '@/features/lists/components/list-item-row';
import { ListMembersSheet } from '@/features/lists/components/list-members-sheet';
import { useRemoveListItem } from '@/features/lists/hooks/use-list-item-mutations';
import { useDeleteList } from '@/features/lists/hooks/use-list-mutations';
import type { ListItem } from '@/features/lists/types';
import { useAuth } from '@/hooks/use-auth';
import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { router, useLocalSearchParams } from 'expo-router';
import React, { useCallback, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  Text,
  View,
} from 'react-native';
import { confirmDialog } from '@/utils/dialog';

type SortBy = 'created_at' | 'name' | 'business_name';

const SORT_OPTIONS: Array<{ value: SortBy; label: string }> = [
  { value: 'created_at', label: 'Date Added' },
  { value: 'name', label: 'Alphabetical' },
  { value: 'business_name', label: 'By Restaurant' },
];

export default function ListDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const theme = useAppTheme();
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<SortBy>('created_at');
  const [membersSheetVisible, setMembersSheetVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editNoteItem, setEditNoteItem] = useState<ListItem | null>(null);

  const {
    data: list,
    isLoading: listLoading,
    error: listError,
    refetch: refetchList,
  } = useQuery({
    ...listDetailQueryOptions(id || ''),
    refetchInterval: 60_000,
  });

  const sortParams: Omit<ListItemsParams, 'page'> = useMemo(
    () => ({ sort_by: sortBy, sort_order: sortBy === 'created_at' ? 'desc' : 'asc' }),
    [sortBy]
  );

  const {
    data: itemsData,
    isLoading: itemsLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...listItemsInfiniteQueryOptions(id || '', sortParams),
    refetchInterval: 60_000,
  });

  const allItems = useMemo(
    () => itemsData?.pages.flatMap((page) => page.items) ?? [],
    [itemsData]
  );

  const isOwner = user?.id === list?.ownerId;
  const canEdit = isOwner; // TODO: check member role for editors

  const deleteListMutation = useDeleteList();
  const removeItemMutation = useRemoveListItem();

  const handleEdit = () => {
    if (!list) return;
    setEditModalVisible(true);
  };

  const handleDelete = () => {
    if (!list) return;
    confirmDialog(
      'Delete List',
      `Are you sure you want to delete "${list.name}"? This cannot be undone.`,
      () => {
        deleteListMutation.mutate(list.id, {
          onSuccess: () => router.back(),
        });
      },
      undefined,
      'Delete',
    );
  };

  const handleRemoveItem = (item: ListItem) => {
    const name = item.menuItemName ?? item.businessName ?? 'this item';
    confirmDialog(
      'Remove Item',
      `Are you sure you want to remove "${name}" from this list?`,
      () => {
        removeItemMutation.mutate({ listId: item.listId, itemId: item.itemId, itemType: item.itemType });
      },
      undefined,
      'Remove',
    );
  };

  const handleEditNote = (item: ListItem) => {
    setEditNoteItem(item);
  };

  const handleEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderItem = useCallback(
    ({ item }: { item: ListItem }) => (
      <ListItemRow
        item={item}
        canEdit={canEdit}
        onRemove={() => handleRemoveItem(item)}
        onEditNote={() => handleEditNote(item)}
      />
    ),
    [canEdit]
  );

  const keyExtractor = useCallback((item: ListItem) => item.id, []);

  const renderFooter = useCallback(() => {
    if (!isFetchingNextPage) return null;
    return (
      <View className="py-4 items-center">
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }, [isFetchingNextPage, theme.colors.primary]);

  return (
    <QueryState
      isLoading={listLoading}
      error={listError}
      onRetry={() => refetchList()}
      loadingMessage="Loading list..."
      fullScreen={false}
    >
      {list && (
        <View className="flex-1">
          <PageTitle title={list.name || 'List'} />
          <ScreenHeader title="List" showBackButton />

          {list.pendingMembership ? (
            <ListInvitationBanner list={list} />
          ) : (
            <>
              <ListDetailHeader
                list={list}
                isOwner={isOwner}
                canEdit={canEdit}
                onEdit={handleEdit}
                onManageMembers={() => setMembersSheetVisible(true)}
                onDelete={handleDelete}
              />

              {/* Sort picker */}
              <View className="flex-row items-center px-4 pb-2" style={{ gap: 8 }}>
                <Text className="text-xs text-muted-foreground">Sort by:</Text>
                {SORT_OPTIONS.map((option) => (
                  <Pressable
                    key={option.value}
                    onPress={() => setSortBy(option.value)}
                    className={`px-2.5 py-1 rounded-full ${
                      sortBy === option.value
                        ? 'bg-primary/10'
                        : 'bg-surface-secondary'
                    }`}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: sortBy === option.value }}
                  >
                    <Text
                      className={`text-xs font-medium ${
                        sortBy === option.value ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>

              {/* Items list */}
              {itemsLoading ? (
                <View className="flex-1 items-center justify-center">
                  <ActivityIndicator color={theme.colors.primary} />
                </View>
              ) : allItems.length === 0 ? (
                <View className="flex-1 items-center justify-center px-6">
                  <Ionicons name="bookmark-outline" size={48} className="text-muted-foreground" />
                  <Text className="text-base font-medium mt-4 text-center text-muted-foreground">
                    This list is empty
                  </Text>
                  <Text className="text-sm mt-2 text-center text-muted-foreground/70">
                    Save menu items or restaurants to add them here.
                  </Text>
                </View>
              ) : (
                <FlatList
                  data={allItems}
                  renderItem={renderItem}
                  keyExtractor={keyExtractor}
                  onEndReached={handleEndReached}
                  onEndReachedThreshold={0.5}
                  ListFooterComponent={renderFooter}
                  contentContainerStyle={{ paddingHorizontal: 16, paddingTop: 8, paddingBottom: 100 }}
                  showsVerticalScrollIndicator={false}
                />
              )}

              {/* Edit list modal */}
              <EditListModal
                visible={editModalVisible}
                list={list}
                onClose={() => setEditModalVisible(false)}
              />

              {/* Edit note modal */}
              <EditNoteModal
                visible={editNoteItem !== null}
                item={editNoteItem}
                onClose={() => setEditNoteItem(null)}
              />

              {/* Members sheet */}
              <ListMembersSheet
                listId={list.id}
                isOwner={isOwner}
                visible={membersSheetVisible}
                onClose={() => setMembersSheetVisible(false)}
              />
            </>
          )}
        </View>
      )}
    </QueryState>
  );
}
